import {
  FiAlertCircle,
  FiAward,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiDownload,
  FiFileText,
  FiMessageSquare,
  FiUsers,
} from "react-icons/fi";
import {
  AttentionTile,
  DashboardPage,
  EmptyState,
  ListRow,
  SectionCard,
  SelectControl,
  StatTile,
  TextLink,
} from "../../../../components/Dashboard";
import { formatNaira, greetingFor } from "../../../../utils/dashboardFormat";
import { STAFF_ROUTES, totalAttention } from "./staffHomeModel";

const ATTENTION_ICONS = {
  overdue: FiClock,
  loans: FiFileText,
  members: FiUsers,
  withdrawals: FiDownload,
};

const PERIOD_OPTIONS = [
  { value: "Weekly", label: "Weekly" },
  { value: "Monthly", label: "Monthly" },
];

/**
 * The staff home page. Presentational: data and the heavier widgets (chart, activity tabs,
 * checklist) arrive as props, so the layout can be tested and previewed without a store.
 */
export default function StaffHomeView({
  firstName,
  branchName,
  now = new Date(),
  banner = null,
  attentionTiles,
  money,
  loans,
  people,
  period,
  onPeriodChange,
  year,
  onYearChange,
  yearOptions,
  chart,
  activity,
  meetings,
  sms,
  subscription,
  checklist = null,
}) {
  const showAttention = Array.isArray(attentionTiles) && attentionTiles.length > 0;
  const attentionTotal = totalAttention(attentionTiles);

  return (
    <DashboardPage>
      <div className="dx-page">
        <div className="dx-head dx-full dx-o-1">
          <div className="dx-greet">
            <h1>
              {greetingFor(now)}
              {firstName ? `, ${firstName}` : ""}
            </h1>
            <p>
              {branchName ? `Showing ${branchName}.` : "Here is how your cooperative is doing."}{" "}
              {now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}
            </p>
          </div>

          <div className="dx-status" aria-label="Plan and SMS balance">
            {subscription ? (
              <div className="dx-status-item">
                <span className={`dx-status-icon dx-status-icon--${subscription.tone}`}>
                  <FiAward aria-hidden="true" size={18} />
                </span>
                <span className="dx-status-text">
                  <span className="dx-status-label">
                    {subscription.planName ? `${subscription.planName} plan` : "Subscription"}
                    {subscription.expiresLabel ? ` · until ${subscription.expiresLabel}` : ""}
                  </span>
                  <span className={`dx-status-value dx-status-value--${subscription.tone}`}>
                    {subscription.label}
                  </span>
                </span>
              </div>
            ) : null}

            <div className="dx-status-item">
              <span className={`dx-status-icon${sms.amount <= 0 ? " dx-status-icon--bad" : ""}`}>
                <FiMessageSquare aria-hidden="true" size={18} />
              </span>
              <span className="dx-status-text">
                <span className="dx-status-label">{sms.amount <= 0 ? "No SMS credit" : "SMS balance"}</span>
                <span className={`dx-status-value${sms.amount <= 0 ? " dx-status-value--bad" : ""}`}>
                  {formatNaira(sms.amount)}
                </span>
              </span>
              <button
                type="button"
                className="dx-btn dx-btn--primary dx-btn--sm"
                onClick={sms.onTopUp}
                disabled={sms.busy}
                aria-label={sms.busy ? undefined : "Top up SMS"}
              >
                {sms.busy ? "Please wait…" : "Top up"}
              </button>
            </div>
          </div>
        </div>

        {banner ? <div className="dx-full dx-o-1">{banner}</div> : null}

        {showAttention ? (
          <section className="dx-full dx-o-2" aria-labelledby="dx-attention-title">
            <h2 id="dx-attention-title" className="dx-section-label">
              Needs your attention
            </h2>
            {attentionTotal === 0 ? (
              <div className="dx-card" role="status">
                <div className="dx-row" style={{ borderTop: 0, minHeight: 0 }}>
                  <span className="dx-row-icon dx-row-icon--ok">
                    <FiCheckCircle aria-hidden="true" size={18} />
                  </span>
                  <span className="dx-row-main">
                    <span className="dx-row-title">You are all caught up</span>
                    <span className="dx-row-sub" style={{ display: "block" }}>
                      No overdue repayments and nothing waiting for approval.
                    </span>
                  </span>
                </div>
              </div>
            ) : (
              <div className="dx-attn-row">
                {attentionTiles.map((tile) => (
                  <AttentionTile
                    key={tile.key}
                    icon={ATTENTION_ICONS[tile.key] || FiAlertCircle}
                    title={tile.title}
                    count={tile.count}
                    detail={tile.detail}
                    tone={tile.tone}
                    to={tile.to}
                  />
                ))}
              </div>
            )}
          </section>
        ) : null}

        <div className="dx-col">
          <div className="dx-tiles dx-tiles--auto dx-o-4" aria-label="Money">
            {money.map((tile) => (
              <StatTile key={tile.key} label={tile.label} value={tile.value} to={tile.to} />
            ))}
          </div>

          <SectionCard
            title="Money in and out"
            className="dx-o-8"
            action={
              <div className="dx-controls">
                {period === "Monthly" ? (
                  <SelectControl
                    id="dx-year"
                    label="Year"
                    value={String(year)}
                    onChange={onYearChange}
                    options={yearOptions.map((y) => ({ value: y, label: y }))}
                  />
                ) : null}
                <SelectControl
                  id="dx-period"
                  label="Period"
                  value={period}
                  onChange={onPeriodChange}
                  options={PERIOD_OPTIONS}
                />
              </div>
            }
          >
            <div className="dx-chart-legend">
              <span>
                <i style={{ background: "var(--color-primary)" }} />
                Credit
              </span>
              <span>
                <i style={{ background: "#82C1D3" }} />
                Debit
              </span>
            </div>
            <div className="dx-chart-box">{chart}</div>
          </SectionCard>

          <SectionCard title="Recent activity" className="dx-o-9">
            {activity}
          </SectionCard>
        </div>

        <div className="dx-col">
          <div className="dx-o-3">{checklist}</div>

          <SectionCard
            title="Loans"
            className="dx-o-5"
            action={<TextLink to={STAFF_ROUTES.loanStatus}>View loans</TextLink>}
          >
            <div className="dx-list">
              {loans.map((row) => (
                <ListRow
                  key={row.key}
                  to={row.to}
                  icon={FiFileText}
                  tone={row.key === "unpaid" ? "warn" : ""}
                  title={row.title}
                  end={row.value}
                />
              ))}
            </div>
          </SectionCard>

          <SectionCard title="People" className="dx-o-6">
            <div className="dx-list">
              {people.map((row) => (
                <ListRow key={row.key} to={row.to} icon={FiUsers} title={row.title} end={row.value} />
              ))}
            </div>
          </SectionCard>

          <SectionCard
            title="Upcoming meetings"
            className="dx-o-7"
            action={
              <SelectControl
                id="dx-meeting-day"
                label="Day"
                value={meetings.selected}
                onChange={meetings.onSelect}
                options={meetings.days.map((day) => ({ value: day, label: day }))}
              />
            }
          >
            {meetings.items.length > 0 ? (
              <div className="dx-list">
                {meetings.items.map((meeting) => (
                  <ListRow
                    key={meeting.id}
                    to={STAFF_ROUTES.events}
                    icon={FiCalendar}
                    title={meeting.name}
                    subtitle={[meeting.dateLabel, meeting.timeLabel].filter(Boolean).join(" · ")}
                  />
                ))}
              </div>
            ) : (
              <EmptyState icon={FiCalendar} title={meetings.emptyTitle}>
                Meetings for the next 7 days appear here. Use the day menu to look ahead.
              </EmptyState>
            )}
          </SectionCard>
        </div>

      </div>
    </DashboardPage>
  );
}
